import { useState } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AssignmentAddIcon from '@mui/icons-material/AssignmentAdd';
import CloseIcon from '@mui/icons-material/Close';
import Widget from '@/components/common/widget.tsx';
import { MediaList, type MediaListContext } from '@/components/events/detail/tabs/common/media/media.list.tsx';
import { type MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import CreateMediaForm from '@/components/media/organization-media-list/create-media.form.tsx';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import type { MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto';
import type { PlaylistMediaRow } from '@/helpers/flow-media-playlist.helper';
import { IgnorableClickAwayListener } from '@/components/common/ignorable-click-away-listener.tsx';
import { Stack, Button, Box, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import type { MediaListDisplayVariant } from '@/components/flows/detail/flow-detail.types';
import type { NonSlotFlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import { narrowHeaderIconButtonSx, widgetHeaderActionSpacing } from '@/components/common/widget-header-action-styles';

export const MediaWidget = ({
  media,
  playlist,
  playlistRows,
  parentId,
  parentType,
  contextParentId,
  contextParentType,
  entityId,
  context,
  placement,
  playlistScope,
  playlistSlotId,
  widgetTitle,
  liveReady = false,
  enablePlayerSocket = false,
  disableBorder = false,
  disableBackground = false,
  disableHover = false,
  disableShadow = false,
  scrollableList = false,
  disabled = false,
  requestButtonLabel,
  addButtonLabel,
  requestSubmitLabel,
  createSubmitLabel,
  disablePlayerQuery = false,
  enabled = true,
  contributorCanAdd = true,
  canReorderPlaylist,
  showRequestButton = true,
  settingsHeaderActions,
  displayVariant = 'default',
  contributorFacing = false,
}: {
  media: MediaDto[];
  playlist?: MediaPlaylistDto;
  playlistRows?: PlaylistMediaRow[];
  parentId: string;
  parentType: MediaParentEntity;
  contextParentId?: string;
  contextParentType?: MediaParentEntity;
  entityId?: string;
  context: MediaListContext;
  liveReady?: boolean;
  placement?: {
    id: string;
    type: 'Slot';
  };
  playlistScope?: string;
  playlistSlotId?: string;
  widgetTitle?: string;
  enablePlayerSocket?: boolean;
  disableBorder?: boolean;
  disableBackground?: boolean;
  disableHover?: boolean;
  disableShadow?: boolean;
  scrollableList?: boolean;
  disabled?: boolean;
  requestButtonLabel?: string;
  addButtonLabel?: string;
  requestSubmitLabel?: string;
  createSubmitLabel?: string;
  disablePlayerQuery?: boolean;
  enabled?: boolean;
  /** When false, hides Create for host contributors; callers should still pass true for managers. */
  contributorCanAdd?: boolean;
  /** When set, gates playlist reorder and advance-mode editing (contributors follow effective allowReorder). */
  canReorderPlaylist?: boolean;
  /** When false, hides the Request action (e.g. pre_flow / after_flow are not host-provided). */
  showRequestButton?: boolean;
  /** Icon menus in the card header (e.g. playlist contributor policy). */
  settingsHeaderActions?: ReactNode;
  displayVariant?: MediaListDisplayVariant;
  contributorFacing?: boolean;
}) => {
  const t = useTranslations();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAddMediaInput, setShowAddMediaInput] = useState(false);
  const [isRequestedMode, setIsRequestedMode] = useState(false);
  const [uploadAfterCreate, setUploadAfterCreate] = useState(true);

  const handleRequestClick = () => {
    // Contributors signal their own media for later upload; operators request contributor media.
    setIsRequestedMode(!contributorFacing);
    setUploadAfterCreate(!contributorFacing);
    setShowAddMediaInput(true);
  };

  const handleAddClick = () => {
    setIsRequestedMode(false);
    setUploadAfterCreate(true);
    setShowAddMediaInput(true);
  };

  const handleCancel = () => {
    setShowAddMediaInput(false);
    setIsRequestedMode(false);
    setUploadAfterCreate(true);
  };

  const isFlowControl = displayVariant === 'flowControl';
  const headerActionSpacing = widgetHeaderActionSpacing(isXs);
  // Slot playlists carry their target through `placement`; the API rejects `playlistScope: 'slot'`.
  const createPlaylistScope =
    playlistScope && playlistScope !== 'slot' ? (playlistScope as NonSlotFlowPlaylistScope) : undefined;

  const renderCancelAction = () =>
    isXs ? (
      <Tooltip title={t('common.cancel')}>
        <IconButton
          id="cancel-media-button"
          size="small"
          color="secondary"
          onClick={(e) => {
            e.stopPropagation();
            handleCancel();
          }}
          data-testid="cancel-media-button"
          sx={narrowHeaderIconButtonSx}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        id="cancel-media-button"
        variant="outlined"
        size="small"
        color="secondary"
        onClick={(e) => {
          e.stopPropagation();
          handleCancel();
        }}
        startIcon={<CloseIcon />}
        data-testid="cancel-media-button"
      >
        {t('common.cancel')}
      </Button>
    );

  const renderRequestAction = () =>
    !showRequestButton ? null : isXs ? (
      <Tooltip title={requestButtonLabel ?? t('common.request')}>
        <IconButton
          size="small"
          color="secondary"
          onClick={handleRequestClick}
          data-testid="request-media-button"
          sx={narrowHeaderIconButtonSx}
        >
          <AssignmentAddIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        onClick={handleRequestClick}
        startIcon={<AssignmentAddIcon />}
        data-testid="request-media-button"
      >
        {requestButtonLabel ?? t('common.request')}
      </Button>
    );

  const renderCreateAction = () =>
    !contributorCanAdd ? null : isXs ? (
      <Tooltip title={addButtonLabel ?? t('common.create')}>
        <IconButton
          size="small"
          color="secondary"
          onClick={handleAddClick}
          data-testid="add-session-media-button"
          sx={narrowHeaderIconButtonSx}
        >
          <AddOutlinedIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        onClick={handleAddClick}
        startIcon={<AddOutlinedIcon />}
        data-testid="add-session-media-button"
      >
        {addButtonLabel ?? t('common.create')}
      </Button>
    );

  const headerActions =
    disabled || isFlowControl ? undefined : showAddMediaInput ? (
      <Stack direction="row" spacing={headerActionSpacing} alignItems="center">
        {renderCancelAction()}
      </Stack>
    ) : showRequestButton || contributorCanAdd || settingsHeaderActions ? (
      <Stack direction="row" spacing={headerActionSpacing} alignItems="center">
        {settingsHeaderActions}
        {renderRequestAction()}
        {renderCreateAction()}
      </Stack>
    ) : undefined;

  const mediaList = (
    <MediaList
      media={media}
      playlist={playlist}
      playlistRows={playlistRows}
      entityId={entityId}
      parentId={parentId}
      parentType={parentType}
      contextParentId={contextParentId ?? parentId}
      contextParentType={contextParentType ?? parentType}
      liveReady={liveReady}
      context={context}
      enablePlayerSocket={enablePlayerSocket}
      disablePlayerQuery={disablePlayerQuery}
      enabled={enabled}
      canReorderPlaylist={canReorderPlaylist}
      displayVariant={displayVariant}
      playlistScope={playlistScope}
      playlistSlotId={playlistSlotId}
      contributorFacing={contributorFacing}
    />
  );

  if (isFlowControl) {
    if (scrollableList) {
      return <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{mediaList}</Box>;
    }
    return mediaList;
  }

  return (
    <Widget
      title={widgetTitle ?? `${media?.length ?? 0} ${t('common.media')}`}
      sx={{
        ...(disableBorder && {
          border: 'none',
        }),
        ...(disableBackground && {
          backgroundColor: 'transparent',
        }),
        ...(disableShadow && {
          boxShadow: 'none',
        }),
        ...(disableHover && {
          '&:hover': {
            boxShadow: disableShadow ? 'none' : 1, // Keep same shadow as default, preventing hover effect
            borderColor: disableBorder ? 'transparent' : 'divider',
          },
        }),
        ...(scrollableList && {
          'display': 'flex',
          'flexDirection': 'column',
          'height': '100%',
          '& .MuiCardContent-root': {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          },
        }),
        ...(disabled && {
          pointerEvents: 'none',
          opacity: 0.5,
          userSelect: 'none',
        }),
      }}
      primaryButton={headerActions}
    >
      {showAddMediaInput && !disabled && (
        <IgnorableClickAwayListener onClickAway={handleCancel} ignoreId="cancel-media-button">
          <div>
            <CreateMediaForm
              parentId={parentId}
              parentType={parentType}
              placement={placement}
              playlistScope={createPlaylistScope}
              isRequested={isRequestedMode}
              uploadAfterCreate={uploadAfterCreate}
              onSuccess={handleCancel}
              requestSubmitLabel={requestSubmitLabel}
              createSubmitLabel={createSubmitLabel}
            />
          </div>
        </IgnorableClickAwayListener>
      )}
      {scrollableList ? <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{mediaList}</Box> : mediaList}
    </Widget>
  );
};
